import { useState, useCallback, useMemo } from 'react'
import { ContactListItem, Scope, normalizeAgeRange } from '@utils'
import { Contact, usePaginateSuggestedContacts } from '@hooks'
import { useSuggestedContactsFiltersStore } from '@store'
import { UseListingContactsResult } from '../../listingContacts/types'

// Hook principal pour le système listingContacts
export const useContactFiltering= (navigation?: any): UseListingContactsResult => {
  // États locaux
  const [scope, setScope] = useState<Scope[]>([])
  // The search text belongs to this screen: it starts empty each time, and
  // the Contacts search keeps its own
  const [searchText, setSearchText] = useState('')
  
  // Filters of this screen, separate from the Contacts ones
  const {
    localFilters,
    reset,
    hasLocalFilters,
    getActiveFiltersCount,
    getFilterSummary
  } = useSuggestedContactsFiltersStore()
   
  // Store global des contacts (personnes)
  const { flatData: contacts, isPending, isFetchingNextPage, fetchNextPage, hasNextPage } = usePaginateSuggestedContacts({
    search: searchText,
    filters: {
      gender: localFilters.gender,
      ageRange: normalizeAgeRange(localFilters.ageRange),
      degree: localFilters.degree ?? undefined,
      residence: (localFilters.residence || []).map((o) => o.id),
      residenceContinents: (localFilters.residenceContinents || []).map((c) => c.id),
      residenceCountries: (localFilters.residenceCountries || []).map((c) => c.id),
      residenceRegions: (localFilters.residenceRegions || []).map((r) => r.id),
      professions: (localFilters.professions || []).map((p) => p.id),
      // Origin, nationality, languages and lived places have no row on this
      // screen (see ListingContactsFilters) and are not sent
    },
  })

  // Filtrage des contacts (logique locale)
  const filteredContacts = useMemo(() => {
    let filtered = contacts
     //TODO
    return filtered
  }, [contacts, searchText, localFilters, hasLocalFilters, scope])
  
  // Statistiques de filtrage
  const filterStats = useMemo(() => ({
    total: contacts.length,
    filtered: filteredContacts.length,
    hasActiveFilters: hasLocalFilters() || searchText.trim().length > 0,
    totalFiltersCount: getActiveFiltersCount() + (searchText.trim().length > 0 ? 1 : 0)
  }), [contacts.length, filteredContacts.length, hasLocalFilters, searchText, getActiveFiltersCount])

  // Actions
  const handleContactPress = useCallback((contact: Contact | ContactListItem) => {
    console.log('Contact sélectionné:', contact.fullName)
    // Navigation vers le profil du contact
    // navigate('ContactProfile', { contactId: contact.id })
  }, [])
  
  const handleFilterPress = useCallback(() => {
    if (navigation) {
      navigation.navigate('ListingContactsFilters')
    }
  }, [navigation])
  
  const handleReset = useCallback(() => {
    setSearchText('')
    reset()
  }, [setSearchText, reset])
  
  // Utilitaires
  const hasActiveFilters = useCallback(() => {
    return hasLocalFilters() || searchText.trim().length > 0
  }, [hasLocalFilters, searchText])
  
  const getActiveFiltersCountTotal = useCallback(() => {
    const baseCount = getActiveFiltersCount()
    const searchCount = searchText.trim().length > 0 ? 1 : 0
    return baseCount + searchCount
  }, [getActiveFiltersCount, searchText])
  
  const getFilterSummaryText = useCallback(() => {
    const parts: string[] = []
    
    // Ajouter la recherche
    if (searchText.trim().length > 0) {
      parts.push(`Recherche: "${searchText}"`)
    }
    
    // Ajouter le résumé des filtres
    const filterSummary = getFilterSummary()
    if (filterSummary) {
      parts.push(filterSummary)
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Aucun filtre actif'
  }, [searchText, getFilterSummary])
  
  return {
    // Données
    contacts,
    scope,
    setScope,
    // États
    searchText,
    // isPending (not isFetching): background refetches and next-page loads must
    // keep the list mounted, otherwise it remounts and scrolls back to the top
    isPending,
    isFetchingNextPage,
    hasNextPage,
    // Statistiques
    filterStats,
    
    // Actions
    fetchNextPage,
    setSearchText,
    handleContactPress,
    handleFilterPress,
    handleReset,
    
    // Utilitaires
    hasActiveFilters,
    getActiveFiltersCount: getActiveFiltersCountTotal,
    getFilterSummary: getFilterSummaryText,
  }
} 